import { StatusControl } from '@/components/StatusControl';
import { useCanEdit } from '@/auth/account';
import { useMemo, useState } from 'react';
import { ListOrdered, Plus } from 'lucide-react';
import type { Investment } from '@/api/types';
import { useGroups, useInvestments, useReorderInvestments, useSummary } from '@/api/hooks';
import { formatMonthRange, formatMoney, formatMoneyShort } from '@/lib/format';
import { UNGROUPED_COLOR } from '@/lib/palette';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { GroupBadge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Field';
import { EmptyState } from '@/components/ui/EmptyState';
import { SortableList, SortableRow } from '@/components/SortableList';
import { InvestmentDialog } from '@/components/InvestmentDialog';
import { PageHeader, QueryState } from '@/components/PageHeader';

export function Priorities() {
  const canEdit = useCanEdit();
  const groups = useGroups();
  const summary = useSummary();
  const reorderInvestments = useReorderInvestments();

  const [groupFilter, setGroupFilter] = useState('');
  const [order, setOrder] = useState<string[] | null>(null);
  const [dialogId, setDialogId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // The server filters, so a group view reorders only that group's slots.
  const query = groupFilter === '' ? '' : `?groupId=${encodeURIComponent(groupFilter)}`;
  const investments = useInvestments(query);

  const currency = summary.data?.currency ?? 'EUR';
  const list = investments.data ?? [];

  const ordered = useMemo(() => {
    if (order === null) return list;
    return order
      .map((id) => list.find((investment) => investment.id === id))
      .filter((investment): investment is Investment => investment !== undefined);
  }, [order, list]);

  /** Running total down the list – "what have we committed to by rank N". */
  const cumulative = useMemo(() => {
    let sum = 0;
    return ordered.map((investment) => {
      if (investment.status !== 'cancelled') sum += investment.effectiveCost;
      return sum;
    });
  }, [ordered]);

  const handleReorder = (ids: string[]) => {
    setOrder(ids);
    reorderInvestments.mutate(ids, { onSettled: () => setOrder(null) });
  };

  const openInvestment = (id: string) => {
    setDialogId(id);
    setDialogOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Prioritäten"
        subtitle="Was zuerst? Ziehe die Einträge in die gewünschte Reihenfolge."
        actions={
          <>
            <label className="sr-only" htmlFor="group-filter">
              Nach Gruppe filtern
            </label>
            <Select
              id="group-filter"
              className="w-auto"
              value={groupFilter}
              onChange={(event) => {
                setGroupFilter(event.target.value);
                setOrder(null);
              }}
            >
              <option value="">Alle Gruppen</option>
              {(groups.data ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
            {canEdit ? (
            <Button
              variant="primary"
              onClick={() => {
                setDialogId(null);
                setDialogOpen(true);
              }}
            >
              <Plus size={15} />
              Neue Investition
            </Button>
            ) : null}
          </>
        }
      />

      <QueryState
        isLoading={investments.isLoading}
        error={(investments.error as Error | null) ?? null}
      >
        {ordered.length === 0 ? (
          <Card>
            <EmptyState
              icon={<ListOrdered size={24} />}
              title="Nichts zu priorisieren"
              description="Lege zuerst Investitionen an – danach lässt sich hier ihre Reihenfolge festlegen."
            />
          </Card>
        ) : (
          <Card className="px-2 py-2">
            <SortableList items={ordered} onReorder={handleReorder} className="gap-1">
              {(investment, index) => (
                <SortableRow
                  key={investment.id}
                  id={investment.id}
                  handleLabel={investment.title}
                >
                  <div className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-[var(--surface-2)]">
                  <button
                    type="button"
                    onClick={() => openInvestment(investment.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <span className="num grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--surface-3)] text-xs font-semibold text-[var(--ink-2)]">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{investment.title}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {investment.groupName ? (
                          <GroupBadge
                            name={investment.groupName}
                            color={investment.groupColor ?? UNGROUPED_COLOR}
                          />
                        ) : (
                          <GroupBadge name="Ohne Gruppe" color={UNGROUPED_COLOR} />
                        )}
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {formatMonthRange(investment.plannedFrom, investment.plannedTo)}
                        </span>
                      </div>
                    </div>
                  </button>
                  <StatusControl investment={investment} />
                  <button type="button" tabIndex={-1} onClick={() => openInvestment(investment.id)} className="shrink-0 text-right">
                      <p className="num text-sm font-medium">
                        {formatMoney(investment.effectiveCost, currency)}
                      </p>
                      <p className="num text-[11px] text-[var(--ink-3)]">
                        kumuliert {formatMoneyShort(cumulative[index] ?? 0, currency)}
                      </p>
                  </button>
                  </div>
                </SortableRow>
              )}
            </SortableList>
          </Card>
        )}
      </QueryState>

      <InvestmentDialog
        open={dialogOpen}
        investmentId={dialogId}
        groups={groups.data ?? []}
        currency={currency}
        defaultGroupId={groupFilter === '' ? null : groupFilter}
        onClose={() => {
          setDialogOpen(false);
          setDialogId(null);
        }}
      />
    </>
  );
}
